/**
 * UPDATE EINSPIELEN (.616), aus der Plattform heraus, auch vom Telefon.
 *
 * ORIGINALTEXT MARVIN (03.11.2026)
 *   „dann kann ich wenigstens auch von unterwegs Aktualisierungen einspielen, indem ich die Dateien vom Handy aus
 *    runterlade und dann über die Dateistruktur vom iPhone auswählen kann und hochladen."
 *
 * Die .zip geht an die API (Prüfung: Lieferung, Version), der Wächter auf dem Server spielt sie ein (siehe
 * updatewaechter.sh). Hier: Datei wählen, Fortschritt, Ergebnis, Protokoll. Nach dem Einspielen meldet die
 * Plattform selbst „Neue Version eingespielt" und lädt neu.
 */
import { useEffect, useRef, useState } from 'react';
import { api, zeitpunkt } from '../api.ts';
import { Karte } from './Geruest.tsx';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';

const mb = (b: number) => `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
const nr = (v: string | null | undefined) => String(v ?? '').split('.').pop() ?? '';
/* .621: wie neuer() in api/src/update.ts: Datum vorn, Nummer hinten als Zahl. */
const neuer = (a: string, b: string) => {
  const d = (x: string) => String(x).replace(/\.\d+$/, ''), n = (x: string) => Number(/\.(\d+)$/.exec(String(x))?.[1] ?? 0);
  return d(a) === d(b) ? n(a) > n(b) : d(a) > d(b);
};

export function Update() {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [datei, setDatei] = useState<File | null>(null);
  const [frage, setFrage] = useState(false);
  const feld = useRef<HTMLInputElement>(null);
  const laden = () => api.updateStand().then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { void laden(); }, []);
  useFrisch(laden);
  /* Solange etwas angefordert ist oder läuft, alle 3 Sekunden nachsehen. */
  useEffect(() => {
    if (!d?.angefordert && !d?.lauf) return;
    const t = setInterval(laden, 3000);
    return () => clearInterval(t);
  }, [d?.angefordert, d?.lauf]);

  async function hochladen() {
    if (!datei) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const r = await api.updateHochladen(datei);
      setHinweis(r.hinweis ?? 'Hochgeladen.');
      setDatei(null); if (feld.current) feld.current.value = '';
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /* .621: VON GITHUB EINSPIELEN. Marvin, 03.11.2026: „Ja baue es so. Wo soll ich dann einspielen?" Hier, mit einer
     Rückfrage im Fenster (kein Browser-Popup). Es wird genau der Stand eingespielt, den die Karte zeigt. */
  async function vonGithub() {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const r = await api.updateGithubEinspielen(d.github.commit);
      setHinweis(r.hinweis ?? 'Angefordert.'); setFrage(false);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); setFrage(false); } finally { setLaeuft(false); }
  }
  const g = d?.github;
  const gNeu = !!g?.nummer && !!d?.laufend && neuer(String(g.nummer), String(d.laufend));
  const e = d?.ergebnis;
  return (
    <Karte titel="Update einspielen">
      <p className="klein leise" style={{ marginTop: 0 }}>
        Läuft: Version {nr(d?.laufend)}. Die Datei mds-ftth-setup.zip hier wählen (am Telefon aus „Dateien“), dann spielt der Server sie von selbst ein: ein bis zwei Minuten, dabei kurz Wartungsmodus. Das Ergebnis kommt per Telegram.
      </p>
      <Fehlerkasten lage={fehler} />
      {hinweis && <div className="hinweis erledigt">{hinweis}</div>}
      {d && d.waechter_fehlt && !d.lauf && (
        <div className="hinweis warnung">
          <b>Der Update-Wächter auf dem Server meldet sich nicht</b>{d.waechter?.am ? ` (zuletzt ${zeitpunkt(d.waechter.am)})` : ' (noch nie)'}.
          Hochladen geht, eingespielt wird erst, wenn er läuft. Einmal auf dem Server: <code>cd /opt/mds && bash updatewaechter.sh --einrichten</code> (ab Version 616 macht das aktualisieren.sh von selbst).
        </div>
      )}
      <div className={'hinweis ' + (gNeu ? 'erledigt' : '')} style={{ marginBottom: 10 }}>
        {!g ? <>
            <b>GitHub ist auf dem Server noch nicht verbunden.</b> Einmal in PuTTY: <code>apt-get install -y git</code>, dann <code>cd /opt/mds && bash updatewaechter.sh --github-einrichten</code> und den angezeigten Schlüssel auf GitHub unter Settings › Deploy keys eintragen.
          </>
          : gNeu ? <>
            <b>Auf GitHub bereit: Version {nr(g.nummer)}</b> (Stand {zeitpunkt(g.commit_am)}).
            {g.was && <div className="klein" style={{ marginTop: 4 }}>{String(g.was).slice(0, 300)}{String(g.was).length > 300 ? ' …' : ''}</div>}
            <div className="reihe" style={{ marginTop: 8 }}>
              <button type="button" className="knopf haupt" disabled={laeuft || !!d?.lauf || !!d?.angefordert} onClick={() => setFrage(true)}>Einspielen</button>
            </div>
          </>
          : <>Auf GitHub: Version {nr(g.nummer)}, nichts Neueres. <span className="klein leise">Zuletzt nachgesehen {zeitpunkt(g.gelesen)}.</span></>}
        {g?.fehler && <div className="klein" style={{ marginTop: 4 }}>GitHub zuletzt nicht erreichbar: {g.fehler}</div>}
      </div>
      {frage && g && (
        <Dialog titel={`Version ${nr(g.nummer)} einspielen?`} breite={460} laeuft={laeuft} zu={() => setFrage(false)}>
          <p style={{ marginTop: 0 }}>Der Server holt Version {nr(g.nummer)} von GitHub und spielt sie ein. Ein bis zwei Minuten, dabei kurz Wartungsmodus. Das Ergebnis kommt per Telegram.</p>
          <div className="reihe" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="knopf" onClick={() => setFrage(false)}>Abbrechen</button>
            <button type="button" className="knopf haupt" disabled={laeuft} onClick={vonGithub}>Jetzt einspielen</button>
          </div>
        </Dialog>
      )}
      <div className="klein leise" style={{ marginBottom: 6 }}>Oder wie bisher die .zip hochladen:</div>
      <div className="reihe" style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input ref={feld} type="file" accept=".zip,application/zip,application/x-zip-compressed" disabled={laeuft || !!d?.lauf}
               onChange={(ev) => setDatei(ev.target.files?.[0] ?? null)} aria-label="Update-Datei (.zip)" />
        <button type="button" className="knopf haupt" disabled={!datei || laeuft || !!d?.lauf || !!d?.angefordert} onClick={hochladen}>
          {laeuft ? 'Lädt hoch …' : 'Hochladen und einspielen'}
        </button>
        {datei && <span className="klein leise">{datei.name}, {mb(datei.size)}</span>}
      </div>
      {d?.angefordert && !d.lauf && <div className="hinweis" style={{ marginTop: 8 }}>Version {nr(d.angefordert.nummer)} liegt bereit (hochgeladen {zeitpunkt(d.angefordert.am)}{d.angefordert.von ? ` von ${d.angefordert.von}` : ''}). Der Wächter nimmt sie in wenigen Sekunden.</div>}
      {d?.lauf && <div className="hinweis" style={{ marginTop: 8 }}><b>Version {nr(d.lauf.nummer)} wird eingespielt</b>, seit {zeitpunkt(d.lauf.begonnen)}. Gleich kurz Wartungsmodus, die Seite lädt danach von selbst neu.</div>}
      {e && !d?.lauf && !d?.angefordert && (
        <div className={'hinweis ' + (e.ok ? 'erledigt' : 'warnung')} style={{ marginTop: 8 }}>
          <b>{e.ok ? `Version ${nr(e.nummer)} eingespielt` : `Version ${nr(e.nummer)} NICHT eingespielt`}</b> ({zeitpunkt(e.am)}, {e.dauer_s} s)
          {!e.ok && <div className="klein">{e.fehler ?? 'ohne Angabe'}</div>}
        </div>
      )}
      {!!d?.protokoll?.length && (
        <details style={{ marginTop: 6 }} open={!!d?.lauf || (e && !e.ok)}>
          <summary className="klein">Protokoll des letzten Einspielens (letzte {d.protokoll.length} Zeilen)</summary>
          <div className="klein" style={{ fontFamily: 'var(--mono, monospace)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}>
            {d.protokoll.map((z: string, i: number) => <div key={i} style={{ color: /ROT/.test(z) ? 'var(--rot, #a33)' : undefined }}>{z.replace(/\x1b\[[0-9;]*m/g, '')}</div>)}
          </div>
        </details>
      )}
    </Karte>
  );
}
